import React, { useState, useRef, useEffect } from 'react';
import cls from 'classnames';
import { queryArchivesById, queryFileList } from '../../service';
import { htmltoStr } from '../../utils';

export default function Music({ setShow, id, MData }) {
    const [isPlay, setIsPlay] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);
    const audioRef = useRef(null);
    const progressRef = useRef(null);
    const isDragging = useRef(false);
    const [data, setData] = useState();
    const [filedata, setFileData] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const audioStatusRef = useRef(false); // 实时音频状态（关键优化）

    // 根据id找到当前音乐的索引
    useEffect(() => {
        if (id && MData.length > 0) {
            const index = MData.findIndex(item => item.id === id);
            setCurrentIndex(index);
        }
    }, [id, MData]);

    // 加载音乐数据
    useEffect(() => {
        if (currentIndex !== -1) {
            const currentMusic = MData[currentIndex];
            queryArchivesById(currentMusic.id).then(res => {
                setData(res);
                queryFileList({
                    pagination: { size: 0, page: 0 },
                    params: {
                        groupId: res?.groupId,
                        code: "CMS_ARCHIVES_FILE"
                    }
                }).then(res1 => {
                    setFileData(res1?.records || []);
                    // 重置音频状态
                    audioStatusRef.current = false;
                    setIsPlay(false);
                });
            });
        }
    }, [currentIndex]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // 同步音频状态到 ref
        const handlePlay = () => {
            audioStatusRef.current = true;
            setIsPlay(true);
        };

        const handlePause = () => {
            audioStatusRef.current = false;
            setIsPlay(false);
        };

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('loadedmetadata', () => setDuration(audio.duration));
        audio.addEventListener('timeupdate', () => {
            if (!isDragging.current) {
                setCurrentTime(audio.currentTime);
                setProgress((audio.currentTime / audio.duration) * 100 || 0);
            }
        });
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('loadedmetadata', () => { });
            audio.removeEventListener('timeupdate', () => { });
            audio.removeEventListener('ended', handleEnded);
        };
    }, [filedata]);

    // 切换到上一曲/下一曲
    const switchMusic = (direction) => {
        if (MData.length === 0 || currentIndex === -1) return;

        let newIndex;
        if (direction === 'prev') {
            newIndex = (currentIndex - 1 + MData.length) % MData.length;
        } else {
            newIndex = (currentIndex + 1) % MData.length;
        }

        setCurrentIndex(newIndex);
        setIsPlay(false);
        setCurrentTime(0);
        setProgress(0);
    };

    const handleEnded = () => {
        switchMusic('next');
        setIsPlay(false);
        setCurrentTime(0);
        setProgress(0);
    };

    // 优化后的暂停功能（核心修复）
    const togglePlay = async () => {
        if (!audioRef.current) return;

        try {
            if (audioStatusRef.current) {
                // 暂停逻辑
                audioRef.current.pause();
                audioStatusRef.current = false; // 强制同步状态
            } else {
                // 播放逻辑
                const playPromise = audioRef.current.play();
                if (playPromise) {
                    await playPromise.catch(e => {
                        console.error("播放失败:", e);
                        alert("播放失败：请点击页面任意位置解锁播放");
                    });
                }
            }
        } catch (error) {
            console.error("播放/暂停错误:", error);
            alert("操作失败，请重试");
        }
    };

    // 拖动进度条优化
    const handleProgressAction = (clientX) => {
        if (!progressRef.current || !audioRef.current || !duration) return;

        const rect = progressRef.current.getBoundingClientRect();
        let newX = clientX - rect.left;
        newX = Math.max(0, Math.min(newX, rect.width));

        const newProgress = (newX / rect.width) * 100;
        const newTime = (newProgress / 100) * duration;

        setProgress(newProgress);
        setCurrentTime(newTime);

        if (isDragging.current) {
            audioRef.current.currentTime = newTime;
        }
    };

    const startDrag = (e) => {
        isDragging.current = true;
        handleProgressAction(e.clientX || e.touches[0].clientX);
    };

    const handleDrag = (e) => {
        if (isDragging.current) {
            e.preventDefault();
            handleProgressAction(e.clientX || e.touches[0].clientX);
        }
    };

    const endDrag = () => {
        if (isDragging.current && audioRef.current) {
            audioRef.current.currentTime = (progress / 100) * duration;
            isDragging.current = false;
            // 拖动结束后恢复播放（如果之前是播放状态）
            if (audioStatusRef.current && !audioRef.current.paused) {
                audioRef.current.play().catch(e => console.error("恢复播放失败:", e));
            }
        }
    };

    const formatTime = (seconds) => {
        if (isNaN(seconds)) return "0:00";
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = Math.floor(seconds % 60);
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    // 触摸优化：增大可点击区域
    const ButtonWithTouchArea = ({ className, onClick, children }) => (
        <div
            className="touch-area"
            onClick={onClick}
            onTouchEnd={(e) => {
                e.preventDefault(); // 防止触摸事件触发两次
                onClick();
            }}
        >
            <div className={className}>{children}</div>
        </div>
    );

    return (
        <div className='common-page music-page'>
            {filedata?.[0]?.filePath && (
                <audio
                    ref={audioRef}
                    src={filedata[0].filePath}
                    preload="metadata"
                />
            )}

            <div className="musicimg">
                <div className={cls("circle", { "animate": isPlay })}>
                    <img src={window.ENV_IMG_PREURL + data?.imgSrc} alt="" />
                </div>
                <div className="title">{data?.title}</div>
            </div>

            <div className="txt">
                <p>{htmltoStr(data?.content)}</p>
                <p>
                    <span className='span1'>演奏者：</span>
                    <span className='span2'>{data?.extension1}</span>
                    <span className='span1'>演奏古琴：</span>
                    <span className='span2'>{data?.extension2}</span>
                </p>
            </div>

            <div className="musicbox">
                <div className="btnbox">
                    <ButtonWithTouchArea
                        className="prev"
                        onClick={() => switchMusic('prev')}
                    />
                    <ButtonWithTouchArea
                        className={cls({ "play": !audioStatusRef.current, "stop": audioStatusRef.current })}
                        onClick={togglePlay}
                    />
                    <ButtonWithTouchArea
                        className="next"
                        onClick={() => switchMusic('next')}
                    />
                </div>
                <div className="time">
                    <div className="starttime">{formatTime(currentTime)}</div>
                    <div
                        className="line"
                        ref={progressRef}
                        onClick={(e) => handleProgressAction(e.clientX)}
                        onMouseDown={startDrag}
                        onMouseMove={handleDrag}
                        onMouseUp={endDrag}
                        onMouseLeave={endDrag}
                        onTouchStart={startDrag}
                        onTouchMove={handleDrag}
                        onTouchEnd={endDrag}
                    >
                        <div
                            className="progress"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                    <div className="endtime">{formatTime(duration)}</div>
                </div>
            </div>

            <div
                className="back"
                onClick={() => {
                    setShow(false);
                    if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.currentTime = 0;
                    }
                }}
                onTouchEnd={(e) => {
                    e.preventDefault();
                    setShow(false);
                    if (audioRef.current) {
                        audioRef.current.pause();
                        audioRef.current.currentTime = 0;
                    }
                }}
            ></div>
        </div>
    );
}